(function () {
  'use strict';

  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  var INIT_FLAG = 'data-infinite-scroll-init';
  // Cap how many pages we replay on a back-navigation so a user who
  // scrolled extremely far doesn't trigger dozens of sequential fetches.
  var MAX_RESTORE_PAGES = 30;
  // sessionStorage key prefix for saved scroll/pagination state.
  var STATE_PREFIX = 'cis:';
  // Only honour saved state for a limited time (ms) to avoid stale jumps.
  var STATE_TTL = 30 * 60 * 1000; // 30 min

  // The primary grid on the page — used by the global pagehide handler to
  // know how many pages were loaded when the user navigated away.
  var primaryInstance = null;

  // ── Scroll-restore helpers ──────────────────────────────────────────

  function locationKey() {
    // Distinguishes collections AND filter/sort variants (which live in
    // the query string) so each has its own saved scroll position.
    return STATE_PREFIX + window.location.pathname + window.location.search;
  }

  function saveState(scrollY, lastLoadedPage) {
    try {
      sessionStorage.setItem(locationKey(), JSON.stringify({
        y: Math.round(scrollY),
        page: lastLoadedPage,
        t: Date.now()
      }));
    } catch (e) { /* storage full / disabled — ignore */ }
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(locationKey());
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || typeof s.y !== 'number') return null;
      if (Date.now() - (s.t || 0) > STATE_TTL) return null;
      return s;
    } catch (e) { return null; }
  }

  function isBackForward() {
    // The reliable signal that this load is a Back/Forward navigation.
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type === 'back_forward';
    } catch (e) { /* fall through */ }
    // Legacy fallback: performance.navigation.type === 2 means back/forward.
    return !!(window.performance && performance.navigation &&
      performance.navigation.type === 2);
  }

  // ── Component ───────────────────────────────────────────────────────

  function CollectionInfiniteScroll(grid) {
    this.grid = grid;
    this.section = grid.closest('section-collection');
    this.totalPages = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
    this.currentPage = parseInt(grid.getAttribute('data-current-page'), 10) || 1;
    this.nextPage = this.currentPage + 1;
    this.allFetched = false;
    this.loading = false;
    this.restoring = false;
    this.buffer = [];

    if (!primaryInstance) primaryInstance = this;

    var unavailableItems = grid.querySelectorAll('.Grid__Item[data-available="false"]');
    for (var i = 0; i < unavailableItems.length; i++) {
      this.buffer.push(unavailableItems[i]);
      unavailableItems[i].parentNode.removeChild(unavailableItems[i]);
    }

    var paginationParent = grid.parentNode;
    var pagination = paginationParent && paginationParent.querySelector('.Pagination');
    if (pagination) pagination.style.display = 'none';

    if (this.totalPages <= 1) {
      this._flushBuffer();
      return;
    }

    this._buildSentinel();
    this._observe();
    this._maybeRestore();
  }

  CollectionInfiniteScroll.prototype._buildSentinel = function () {
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'CollectionInfiniteScroll__Sentinel';
    this.sentinel.style.cssText = 'width:100%;height:1px;';

    this.loader = document.createElement('div');
    this.loader.className = 'CollectionInfiniteScroll__Loader';
    this.loader.style.cssText = 'width:100%;text-align:center;padding:24px 12px;font-size:14px;opacity:0.6;';
    this.loader.textContent = '…';

    var parent = this.grid.parentNode;
    parent.insertBefore(this.loader, this.grid.nextSibling);
    parent.insertBefore(this.sentinel, this.loader.nextSibling);
  };

  CollectionInfiniteScroll.prototype._observe = function () {
    var self = this;
    this.observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      if (self.loading || self.allFetched || self.restoring) return;
      self._loadNext();
    }, { rootMargin: '1800px 0px' });
    this.observer.observe(this.sentinel);
  };

  // Fetch exactly one page, append its items, then invoke `onDone`.
  // Shared by normal infinite scroll and the back-navigation restore.
  CollectionInfiniteScroll.prototype._fetchPage = function (onDone) {
    var self = this;
    if (self.loading || self.allFetched) { if (onDone) onDone(); return; }
    self.loading = true;

    var url;
    try {
      url = new URL(window.location.href);
    } catch (e) {
      url = new URL(window.location.pathname, window.location.origin);
    }
    url.searchParams.set('page', String(self.nextPage));
    var sectionId = self.section && self.section.getAttribute('data-section-id');
    if (sectionId) url.searchParams.set('section_id', sectionId);

    fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fetchedGrid = tmp.querySelector('.Grid[data-stock-sort-grid]');
        if (!fetchedGrid) {
          self.allFetched = true;
          return;
        }
        var items = fetchedGrid.querySelectorAll('.Grid__Item');
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          if (item.getAttribute('data-available') === 'false') {
            self.buffer.push(item);
          } else {
            self.grid.appendChild(item);
          }
        }
        self.nextPage += 1;
        if (self.nextPage > self.totalPages) {
          self.allFetched = true;
        }
      })
      .catch(function (err) {
        if (window.console) console.warn('[InfiniteScroll]', err);
        self.allFetched = true;
      })
      .then(function () {
        self.loading = false;
        if (self.allFetched) {
          self._flushBuffer();
          if (self.observer) self.observer.disconnect();
          if (self.sentinel && self.sentinel.parentNode) self.sentinel.parentNode.removeChild(self.sentinel);
          if (self.loader && self.loader.parentNode) self.loader.parentNode.removeChild(self.loader);
        }
        if (onDone) onDone();
      });
  };

  CollectionInfiniteScroll.prototype._loadNext = function () {
    var self = this;
    this._fetchPage(function () {
      if (self.allFetched || self.restoring) return;
      // Keep loading while the sentinel is still within the prefetch band.
      if (self.sentinel) {
        var rect = self.sentinel.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < vh + 1800) {
          setTimeout(function () { self._loadNext(); }, 80);
        }
      }
    });
  };

  // On a Back/Forward navigation, replay the pages the user had loaded and
  // then jump back to their previous scroll position.
  CollectionInfiniteScroll.prototype._maybeRestore = function () {
    if (this !== primaryInstance) return;
    if (!isBackForward()) return;
    var saved = readState();
    if (!saved) return;

    var targetPage = Math.min(saved.page || 1, this.totalPages, MAX_RESTORE_PAGES);
    this.restoring = true;
    this._restoreTo(targetPage, saved.y);
  };

  CollectionInfiniteScroll.prototype._restoreTo = function (targetPage, scrollY) {
    var self = this;
    function step() {
      if (self.allFetched || self.nextPage > targetPage) {
        self.restoring = false;
        self._applyScroll(scrollY);
        // Resume normal lazy-loading from wherever the user now is.
        if (!self.allFetched && self.observer && self.sentinel) {
          var rect = self.sentinel.getBoundingClientRect();
          var vh = window.innerHeight || document.documentElement.clientHeight;
          if (rect.top < vh + 1800) self._loadNext();
        }
        return;
      }
      self._fetchPage(step);
    }
    step();
  };

  // Scroll to the saved Y, re-applying a couple of times to absorb any
  // late reflow (e.g. images settling into their aspect-ratio boxes).
  CollectionInfiniteScroll.prototype._applyScroll = function (y) {
    window.scrollTo(0, y);
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { window.scrollTo(0, y); });
    }
    setTimeout(function () { window.scrollTo(0, y); }, 120);
    setTimeout(function () { window.scrollTo(0, y); }, 400);
  };

  CollectionInfiniteScroll.prototype._flushBuffer = function () {
    if (!this.buffer.length) return;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < this.buffer.length; i++) {
      frag.appendChild(this.buffer[i]);
    }
    this.grid.appendChild(frag);
    this.buffer = [];
  };

  // ── Persist scroll state when leaving the page ──────────────────────

  function persist() {
    if (!primaryInstance) return;
    // Last successfully loaded page = nextPage - 1 (nextPage starts at 2).
    var lastLoaded = Math.max(1, primaryInstance.nextPage - 1);
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    saveState(y, lastLoaded);
  }

  // pagehide covers link clicks, back/forward and tab close on iOS Safari.
  window.addEventListener('pagehide', persist);
  // Extra safety for mobile: also save when the tab is hidden.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') persist();
  });

  // Scroll to a Y, re-applying a few times to absorb late reflow.
  function applyScrollRetries(y) {
    window.scrollTo(0, y);
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { window.scrollTo(0, y); });
    }
    setTimeout(function () { window.scrollTo(0, y); }, 120);
    setTimeout(function () { window.scrollTo(0, y); }, 400);
  }

  // iOS (Safari + Chrome) restores pages from the back-forward cache:
  // the full DOM — including everything infinite-scroll appended — comes
  // back from a snapshot, but DOMContentLoaded does NOT fire, so the
  // init-time restore never runs and the scroll position is often lost.
  // Catch the bfcache restore explicitly and re-apply the saved scroll.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return; // fresh loads are handled by _maybeRestore
    var saved = readState();
    if (saved) applyScrollRetries(saved.y);
  });

  // ── Bootstrap ───────────────────────────────────────────────────────

  function initAll() {
    var grids = document.querySelectorAll('.Grid[data-stock-sort-grid]:not([' + INIT_FLAG + '])');
    for (var i = 0; i < grids.length; i++) {
      grids[i].setAttribute(INIT_FLAG, '1');
      new CollectionInfiniteScroll(grids[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('theme:loading:end', initAll);
})();
